import { YandexMetrikaAbTestFlagKeys } from '@analytics/const';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AbTestAnalyticsService, YandexMetrikaService } from '@analytics/feature';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CatalogActions } from '@catalog/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OccProductSearchService } from '@search/data-access';
import { UrlNavigatorService } from '@shared/services';
import { ProductActions, ProductSearchPage, SearchConfig, normalizeHttpError } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class ProductSearchEffects {
  productSearchRedirect$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.SEARCH_PRODUCTS_SUCCESS),
        filter((action: ProductActions.SearchProductsSuccess) => Boolean(action.payload.keywordRedirectUrl)),
        map((action) => {
          const { keywordRedirectUrl, freeTextSearch } = action.payload;

          this.urlNavigatorService.navigate(keywordRedirectUrl, {
            queryParams: { freeTextSearch },
          });

          return;
        }),
      ),
    { dispatch: false },
  );

  productSearch404$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.SEARCH_PRODUCTS_SUCCESS),
        filter((action: ProductActions.SearchProductsSuccess) => {
          const currentPage = action.payload.pagination?.currentPage ?? 0;
          const totalPages = action.payload.pagination?.totalPages ?? 0;

          return currentPage > totalPages;
        }),
        map(() => {
          this.router.navigate(['/not-found']);
        }),
      ),
    { dispatch: false },
  );

  searchProducts$: Observable<ProductActions.SearchProductsSuccess | ProductActions.SearchProductsFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.SEARCH_PRODUCTS),
        map((action: ProductActions.SearchProducts) => action),
        withLatestFrom(this.getSearchEngineStrategy()),
        switchMap(([action, searchEngineStrategy]) =>
          this.occProductSearchService
            .search(action.payload.queryText, { ...action.payload.searchConfig, searchEngineStrategy })
            .pipe(
              map((data) => new ProductActions.SearchProductsSuccess(data)),
              catchError((error) => of(new ProductActions.SearchProductsFail(normalizeHttpError(error)))),
            ),
        ),
      ),
  );

  loadMoreProduct$: Observable<CatalogActions.LoadMoreProductsSuccess | CatalogActions.LoadMoreProductsFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CatalogActions.LOAD_MORE_PRODUCTS),
        map((action: CatalogActions.LoadMoreProducts) => action),
        withLatestFrom(this.getSearchEngineStrategy()),
        switchMap(([action, searchEngineStrategy]) =>
          this.occProductSearchService
            .search(action.payload.freeTextSearch, { ...action.payload, searchEngineStrategy })
            .pipe(
              map((data: ProductSearchPage) => new CatalogActions.LoadMoreProductsSuccess(data)),
              catchError((error: unknown) => of(new CatalogActions.LoadMoreProductsFail(normalizeHttpError(error)))),
            ),
        ),
      ),
    );

  constructor(
    private actions$: Actions,
    private urlNavigatorService: UrlNavigatorService,
    private occProductSearchService: OccProductSearchService,
    private yandexMetrikaService: YandexMetrikaService,
    private abTestAnalyticsService: AbTestAnalyticsService,
    private router: Router,
  ) {}

  private getSearchEngineStrategy(): Observable<SearchConfig['searchEngineStrategy']> {
    return this.yandexMetrikaService.getFlagValue(YandexMetrikaAbTestFlagKeys.SearchEngineStrategy).pipe(
      map((variant) => {
        this.abTestAnalyticsService.sendEventVariantWasShown(YandexMetrikaAbTestFlagKeys.SearchEngineStrategy, variant);
        switch (variant) {
          case '1':
            return 'ANY_QUERY';
          case '2':
            return 'SD_SOLR';
          default:
            return 'DEFAULT';
        }
      }),
    );
  }
}
